import type { MutationFunctionContext, Query, UseMutationOptions } from '@tanstack/react-query';
import type { AppelApi } from './client.js';
import {
  capturerPortee,
  COMPTE_ANONYME,
  estActuelle,
  exigerActuelle,
  identiteCourante,
  PorteeObsolete,
  PREFIXE_METIER,
  type CapturePortee,
} from '../auth/identite.js';

/**
 * Clé de requête métier, préfixée par l'identité (audit MVP du 23/09, § 2) :
 * `['metier', <compteId>, <galerie>, ...reste]`. Deux comptes, ou deux
 * galeries, ne partagent jamais une entrée du cache ; la purge d'une
 * transition vise `['metier']` d'un seul coup (`auth/identite.ts`).
 *
 * Une invalidation partielle garde la même forme :
 * `cleMetier(galerie, 'oeuvres')` couvre toutes les clés œuvres de la galerie.
 */
export function cleMetier<const T extends readonly unknown[]>(
  galerie: string,
  ...reste: T
): readonly [typeof PREFIXE_METIER, string, string, ...T] {
  const compteId = identiteCourante()?.compteId ?? COMPTE_ANONYME;
  return [PREFIXE_METIER, compteId, galerie, ...reste] as const;
}

/** Le compte d'une clé métier (`['metier', compteId, …]`), hors compte anonyme. */
function compteDeCle(cle: readonly unknown[]): string | null {
  const [prefixe, compte] = cle;
  return prefixe === PREFIXE_METIER && typeof compte === 'string' && compte !== COMPTE_ANONYME
    ? compte
    : null;
}

/**
 * L'appel d'une LECTURE métier (constat N3) : le signal de TanStack et le
 * compte capturé dans la clé même de la requête — celui dont le cache
 * recevra la réponse. Le serveur refuse (`409 compte_change`) si la session
 * n'est plus ce compte : une réponse pour B ne peut plus remplir le cache de A.
 */
export function appelDeRequete(contexte: {
  readonly signal: AbortSignal;
  readonly queryKey: readonly unknown[];
}): AppelApi {
  const compteAttendu = compteDeCle(contexte.queryKey);
  // Sans compte capturé (identité oubliée entre deux rendus), aucune lecture
  // métier ne part : sa réponse n'appartiendrait à personne.
  if (compteAttendu === null) throw new PorteeObsolete();
  return { signal: contexte.signal, compteAttendu };
}

/**
 * `placeholderData` des listes paginées : garde la page précédente pendant
 * le chargement de la suivante, mais SEULEMENT si elle appartient au même
 * compte et à la même galerie. Sans cette garde, un changement de galerie
 * afficherait la liste de l'ancienne le temps que la nouvelle arrive.
 */
export function precedentDansLaPortee(galerie: string) {
  return <TDonnees>(
    precedent: TDonnees | undefined,
    requetePrecedente: Query<TDonnees, Error, TDonnees, readonly unknown[]> | undefined,
  ): TDonnees | undefined => {
    if (!requetePrecedente) return undefined;
    const [prefixe, compte, galeriePrecedente] = requetePrecedente.queryKey;
    const courante = cleMetier(galerie);
    return prefixe === courante[0] && compte === courante[1] && galeriePrecedente === courante[2]
      ? precedent
      : undefined;
  };
}

/**
 * Ce que chaque exécution de mutation a capturé à son lancement, rangé sous
 * le `MutationFunctionContext` de CETTE exécution : TanStack en crée un par
 * `mutate()` et le passe à `onMutate`, à `mutationFn` et aux callbacks de
 * fin. Pas de fermeture sur le rendu : TanStack remplace EN PLACE les options
 * d'une mutation en cours à chaque rendu, et un callback relu sous B aurait
 * capturé B.
 */
const capturesDesMutations = new WeakMap<MutationFunctionContext, CapturePortee>();

/**
 * Garde de portée d'une mutation (relecture du 23/09, constat 1) : la portée
 * et l'époque sont capturées au lancement ; si l'identité change ensuite,
 *
 *  - `mutationFn` n'envoie rien, ou rejette sa réponse arrivée trop tard
 *    (`PorteeObsolete`) — la page de la nouvelle identité n'agit pas dessus ;
 *  - `onSuccess`, `onError` et `onSettled` ne touchent à rien : ni
 *    restauration d'un instantané optimiste, ni `setQueryData`, ni
 *    invalidation sous des clés recalculées pour la nouvelle identité.
 */
/**
 * Options d'une mutation gardée : sa `mutationFn` reçoit, en second argument,
 * l'appel EXPLICITE à transmettre à la fonction d'API — le signal et le
 * compte capturés au lancement (seconde relecture du 23/09).
 */
export type OptionsMutationGardee<TData, TError, TVariables, TOnMutateResult> = Omit<
  UseMutationOptions<TData, TError, TVariables, TOnMutateResult>,
  'mutationFn'
> & {
  mutationFn: (variables: TVariables, appel: AppelApi) => Promise<TData>;
};

export function dansLaPortee<TData, TError, TVariables, TOnMutateResult>(
  options: OptionsMutationGardee<TData, TError, TVariables, TOnMutateResult>,
): UseMutationOptions<TData, TError, TVariables, TOnMutateResult> {
  const { mutationFn, onMutate, onSuccess, onError, onSettled } = options;
  const actuelle = (contexte: MutationFunctionContext): boolean =>
    estActuelle(capturesDesMutations.get(contexte));

  return {
    ...options,
    onMutate: (variables, contexte) => {
      capturesDesMutations.set(contexte, capturerPortee());
      return onMutate?.(variables, contexte) as TOnMutateResult | Promise<TOnMutateResult>;
    },
    mutationFn: async (variables, contexte) => {
      const capture = capturesDesMutations.get(contexte);
      exigerActuelle(capture);
      // Transmis jusqu'au `fetch` : le signal de l'époque capturée (annulé si
      // l'identité change pendant la requête) et le compte de lancement, que
      // l'API vérifie (`X-Artosera-Compte-Attendu`).
      const resultat = await mutationFn(variables, {
        signal: capture.signal,
        compteAttendu: capture.compteId,
      });
      exigerActuelle(capture);
      return resultat;
    },
    onSuccess: (donnees, variables, resultatOnMutate, contexte) => {
      if (!actuelle(contexte)) return undefined;
      return onSuccess?.(donnees, variables, resultatOnMutate, contexte);
    },
    onError: (erreur, variables, resultatOnMutate, contexte) => {
      if (!actuelle(contexte)) return undefined;
      return onError?.(erreur, variables, resultatOnMutate, contexte);
    },
    onSettled: (donnees, erreur, variables, resultatOnMutate, contexte) => {
      if (!actuelle(contexte)) return undefined;
      return onSettled?.(donnees, erreur, variables, resultatOnMutate, contexte);
    },
  };
}

/**
 * Un appel lancé HORS de TanStack (export, PDF, téléchargement), dans la
 * portée COURANTE (quatrième relecture, 2) : compte attendu et signal de
 * l'époque capturés au lancement ; le résultat n'est rendu que si la portée
 * n'a pas changé entre-temps — sinon `PorteeObsolete`, et l'appelant
 * n'enregistre ni n'ouvre rien.
 */
export async function dansLaPorteeCourante<T>(
  executer: (appel: AppelApi) => Promise<T>,
): Promise<T> {
  const capture = capturerPortee();
  if (capture.compteId === null) throw new PorteeObsolete();
  const resultat = await executer({ signal: capture.signal, compteAttendu: capture.compteId });
  exigerActuelle(capture);
  return resultat;
}
